import { ShieldCheck, Filter, Globe, Eye, Ban, Users, BarChart3, Radio, Clock } from "lucide-react";
import { motion } from "framer-motion";

const sourceLogos = ["Reuters", "AP News", "Al Jazeera", "BBC World", "CNN"];

const scenarios = [
  "A family with relatives abroad tracks escalation patterns around key cities and critical infrastructure before planning travel or relocation.",
  "An expat in the Gulf monitors missile and drone activity within a radius of their home and workplace.",
  "A freelance journalist uses the event history to understand what has happened in a region over the last 7–30 days before heading into the field.",
];

const TrustCredibilitySection = () => {
  return (
    <section className="border-b border-border">
      <div className="container mx-auto px-4 py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mx-auto max-w-3xl"
        >
          {/* Header */}
          <div className="mb-10 text-center">
            <h2 className="mb-2 text-2xl font-bold text-foreground md:text-3xl">
              Trust &amp; Credibility
            </h2>
            <p className="mx-auto max-w-xl text-sm text-muted-foreground">
              In fast-moving conflicts, bad information is worse than no information.
              Global Conflict Monitor prioritizes accuracy, transparency and calm
              decision‑making over clicks and sensationalism.
            </p>
          </div>

          {/* ── How we source and filter ── */}
          <div className="mb-8">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-foreground">
              <Filter className="h-4 w-4 text-primary" />
              How we source and filter data
            </h3>
            <div className="space-y-3 rounded-lg border border-border bg-card p-5">
              <p className="text-sm text-muted-foreground">
                Data sourced from public OSINT and major news wires, including Reuters,
                AP, BBC, Al Jazeera and CNN{" "}
                <span className="text-muted-foreground/60 italic">
                  (no formal affiliation or endorsement implied)
                </span>.
              </p>
              <p className="text-sm text-muted-foreground">
                Events are automatically de‑duplicated using coordinate matching,
                timestamp proximity and text similarity, then scored for confidence on a
                0–100 scale based on the number of independent sources confirming each
                incident.
              </p>
              <p className="text-sm text-muted-foreground">
                Only high‑confidence, cross‑checked events make it onto the live map and
                alerts feed. Up to 90 % of incoming signals are filtered out as noise
                before they ever reach your screen.
              </p>
              <p className="flex items-center gap-2 text-sm text-muted-foreground/70 italic">
                <Clock className="h-3.5 w-3.5 shrink-0 text-primary/60" />
                Feed latency typically 1–5 minutes after initial wire publish for Premium users.
              </p>
            </div>
          </div>

          {/* ── Designed for real people ── */}
          <div className="mb-8">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-foreground">
              <Users className="h-4 w-4 text-primary" />
              Designed for real people in high‑risk regions
            </h3>
            <div className="grid gap-3 md:grid-cols-3">
              {[
                {
                  icon: Ban,
                  text: "We do not republish social media rumors — only verified OSINT and wire‑service reporting.",
                },
                {
                  icon: Eye,
                  text: "Interface optimized for high‑stress moments: clear map, clear labels, no clutter.",
                },
                {
                  icon: Radio,
                  text: "Designed with input from journalists, security‑minded expats and families near active conflict zones.",
                },
              ].map((item) => (
                <div
                  key={item.text}
                  className="flex flex-col items-start gap-2 rounded-lg border border-border bg-card/50 p-4"
                >
                  <item.icon className="h-4 w-4 text-primary" />
                  <p className="text-xs font-medium leading-relaxed text-muted-foreground">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Operational scale ── */}
          <div className="mb-8">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-foreground">
              <BarChart3 className="h-4 w-4 text-primary" />
              Operational scale you can rely on
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { value: "190+", label: "Countries monitored 24/7" },
                { value: "1000s", label: "Events processed daily" },
                { value: "<2 min", label: "Typical premium alert latency" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-lg border border-border bg-card p-4 text-center"
                >
                  <div className="font-mono text-xl font-bold text-primary">
                    {s.value}
                  </div>
                  <p className="mt-1 text-[11px] leading-tight text-muted-foreground">
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Example scenarios ── */}
          <div className="mb-8">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-foreground">
              <Globe className="h-4 w-4 text-primary" />
              Example scenarios
            </h3>
            <div className="space-y-3">
              {scenarios.map((s, i) => (
                <div
                  key={i}
                  className="flex gap-3 rounded-lg border border-border bg-card/50 p-4"
                >
                  <span className="mt-0.5 font-mono text-xs font-bold text-primary">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {s}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Independence ── */}
          <div className="mb-8">
            <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold uppercase tracking-wider text-foreground">
              <ShieldCheck className="h-4 w-4 text-primary" />
              Independent, transparent, focused on your safety
            </h3>
            <div className="rounded-lg border border-border bg-card p-5">
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  We do not accept funding from governments, political parties or
                  militaries.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  We do not publish rumors, unverified social media posts or
                  user‑generated footage as facts.
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                  Global Conflict Monitor is a decision‑support tool — it does not
                  replace official guidance from authorities or emergency services.
                </li>
              </ul>
            </div>
          </div>

          {/* ── Source logos ── */}
          <div className="text-center">
            <p className="mb-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              Sourcing from public OSINT and major news wires
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6">
              {sourceLogos.map((s) => (
                <span
                  key={s}
                  className="font-mono text-sm font-semibold text-muted-foreground/50"
                >
                  {s}
                </span>
              ))}
            </div>
            <p className="mt-2 text-[10px] italic text-muted-foreground/50">
              No formal affiliation or endorsement implied
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustCredibilitySection;
